import { isNativePlatform } from '@lib/platform';
import { showAnnouncement } from '@components/ui/announcement';
import { registerSW } from 'virtual:pwa-register';
import { handleChunkLoadError } from '@utils/chunkLoadErrorHandler';
import { logger } from '@utils/logger';
import { SW_MESSAGE } from '@lib/pwa/sw-messages';

let isUpdatePromptOpen = false;
let updateCheckInterval: ReturnType<typeof setInterval> | null = null;
let repromptTimeout: ReturnType<typeof setTimeout> | null = null;
const RE_PROMPT_DELAY_MS = 30 * 60 * 1000; // 30 minutes

/**
 * Registers the service worker and surfaces the "update available" prompt as an announcement.
 * Returns the updater function produced by `registerSW` so callers can trigger a manual refresh if needed.
 */
export const registerServiceWorkerPrompts = () => {
  // Skip SW registration on native platforms -- assets are bundled in the native app
  // and updates come from the App Store
  if (isNativePlatform()) {
    // eslint-disable-next-line @typescript-eslint/no-empty-function -- no-op cleanup for native
    return () => {};
  }

  const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null;
  // Clear any existing timers to prevent duplicates
  if (updateCheckInterval) {
    clearInterval(updateCheckInterval);
    updateCheckInterval = null;
  }
  if (repromptTimeout) {
    clearTimeout(repromptTimeout);
    repromptTimeout = null;
  }
  /** Re-prompt after 30 minutes if a waiting SW still exists. */
  const scheduleReprompt = (updateSW: (reloadPage?: boolean) => Promise<void>) => {
    if (repromptTimeout) clearTimeout(repromptTimeout);
    repromptTimeout = setTimeout(() => {
      void navigator.serviceWorker.ready
        .then(registration => {
          if (registration.waiting) {
            showUpdateAnnouncement(updateSW);
          }
        })
        .catch(() => {
          // SW not available, skip re-prompt
        });
    }, RE_PROMPT_DELAY_MS);
  };

  /**
   * Shows the "update available" announcement with Refresh/Later buttons.
   * Extracted so it can be called both from onNeedRefresh and from the re-prompt timer.
   */
  const showUpdateAnnouncement = (updateSW: (reloadPage?: boolean) => Promise<void>) => {
    if (isUpdatePromptOpen) {
      return;
    }
    isUpdatePromptOpen = true;

    showAnnouncement({
      id: 'app_update',
      title: 'A fresh Meetball just landed',
      body: "We've been building. Refresh to get the newest version.",
      primary: {
        label: 'Refresh',
        onClick: () => {
          isUpdatePromptOpen = false;
          if (repromptTimeout) clearTimeout(repromptTimeout);
          updateSW(true).catch((error: unknown) => {
            logger.error('Failed to update service worker', error);
          });
        },
      },
      secondary: {
        label: 'Later',
        onClick: () => {
          isUpdatePromptOpen = false;
          scheduleReprompt(updateSW);
        },
      },
      // Swiping it away counts as Later, so the prompt still comes back.
      onDismiss: () => {
        isUpdatePromptOpen = false;
        scheduleReprompt(updateSW);
      },
    });
  };

  const updateServiceWorker = registerSW({
    onNeedRefresh() {
      logger.info('NEED REFRESH');
      showUpdateAnnouncement(updateServiceWorker);
    },
  });

  // Service worker update checks and stale asset detection
  if ('serviceWorker' in navigator) {
    // Periodic update checks
    navigator.serviceWorker.ready
      .then(registration => {
        // Check for updates immediately on app load
        registration.update().catch((err: unknown) => {
          logger.error('SW update check failed:', err);
        });

        // Check periodically (every 10 minutes)
        updateCheckInterval = setInterval(
          () => {
            registration.update().catch((err: unknown) => {
              logger.error('Periodic SW update check failed:', err);
            });
          },
          10 * 60 * 1000,
        );
      })
      .catch((err: unknown) => {
        logger.error('SW registration failed:', err);
      });

    // Listen for controller changes to notify user
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      logger.info('Service worker controller changed - new version active');
    });

    // Listen for stale assets detection from service worker
    // This happens when old chunk hashes are requested but no longer exist
    navigator.serviceWorker.addEventListener('message', event => {
      const data: unknown = event.data;
      if (isRecord(data) && data.type === SW_MESSAGE.STALE_ASSETS_DETECTED) {
        logger.warn('Stale assets detected - triggering chunk error recovery');
        // Use shared handler for consistent recovery behavior (cache clear, SW unregister, reload)
        void handleChunkLoadError(new Error('Stale assets detected by service worker'));
      }
    });
  }

  return updateServiceWorker;
};
